import { useEffect, useState } from "react";
import {
  Add20Regular,
  ArrowDown20Regular,
  ArrowUp20Regular,
  Checkmark20Regular,
  Delete20Regular,
  Dismiss20Regular,
  Edit20Regular,
} from "@fluentui/react-icons";
import {
  createPaymentStatus,
  deletePaymentStatus,
  getPaymentStatuses,
  PaymentStatusApiError,
  updatePaymentStatus,
  type PaymentStatus,
} from "../settings/paymentStatusesApi";

export default function PaymentStatusSettingsTab() {
  const [statuses, setStatuses] = useState<PaymentStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");

  const load = async () => {
    try {
      const data = await getPaymentStatuses();
      setStatuses(data);
      setError(null);
    } catch {
      setError("Não foi possível carregar os estados de pagamento.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showError = (err: unknown, fallback: string) => {
    setError(err instanceof PaymentStatusApiError ? err.message : fallback);
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name || isBusy) return;

    setIsBusy(true);
    setError(null);
    try {
      await createPaymentStatus(name);
      setNewName("");
      await load();
    } catch (err) {
      showError(err, "Não foi possível criar o estado.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleStartEdit = (status: PaymentStatus) => {
    setEditingId(status.id);
    setEditingName(status.name);
    setError(null);
  };

  const handleSaveEdit = async (status: PaymentStatus) => {
    const name = editingName.trim();
    if (!name || isBusy) return;

    setIsBusy(true);
    setError(null);
    try {
      await updatePaymentStatus(status.id, { name });
      setEditingId(null);
      await load();
    } catch (err) {
      showError(err, "Não foi possível guardar o estado.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async (status: PaymentStatus) => {
    if (isBusy) return;

    setIsBusy(true);
    setError(null);
    try {
      await deletePaymentStatus(status.id);
      await load();
    } catch (err) {
      // 400 = estado em uso → mostra a mensagem do servidor.
      showError(err, "Não foi possível eliminar o estado.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const swapIndex = index + direction;
    if (isBusy || swapIndex < 0 || swapIndex >= statuses.length) return;

    const current = statuses[index];
    const neighbour = statuses[swapIndex];

    setIsBusy(true);
    setError(null);
    try {
      await Promise.all([
        updatePaymentStatus(current.id, {
          name: current.name,
          sortOrder: neighbour.sortOrder,
        }),
        updatePaymentStatus(neighbour.id, {
          name: neighbour.name,
          sortOrder: current.sortOrder,
        }),
      ]);
      await load();
    } catch (err) {
      showError(err, "Não foi possível reordenar os estados.");
    } finally {
      setIsBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="text-secondary-text py-8 text-center"
        style={{ fontSize: "var(--text-base)" }}
      >
        A carregar estados...
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-4">
        <p
          className="text-secondary-text"
          style={{ fontSize: "var(--text-caption)" }}
        >
          Gerir os estados de pagamento usados na coluna Estado das faturas.
          Não é possível eliminar um estado enquanto houver faturas com ele.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <input
          type="text"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleAdd();
          }}
          placeholder="Nome do novo estado (ex.: Por pagar)"
          className="flex-1 px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          style={{
            borderRadius: "var(--radius-button)",
            fontSize: "var(--text-base)",
          }}
        />
        <button
          onClick={handleAdd}
          disabled={isBusy || newName.trim().length === 0}
          className="px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            borderRadius: "var(--radius-button)",
            fontSize: "var(--text-caption)",
          }}
        >
          <Add20Regular className="w-4 h-4" />
          Adicionar
        </button>
      </div>

      {error && (
        <div
          className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      <div
        className="border border-border rounded"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full">
          <thead>
            <tr
              className="border-b border-border bg-secondary/50"
              style={{ fontSize: "var(--text-caption)" }}
            >
              <th
                className="text-left p-4 text-foreground"
                style={{ fontWeight: "var(--font-weight-semi-bold)" }}
              >
                Nome
              </th>
              <th
                className="text-right p-4 text-foreground"
                style={{
                  fontWeight: "var(--font-weight-semi-bold)",
                  width: "200px",
                }}
              >
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {statuses.map((status, index) => {
              const isEditing = editingId === status.id;

              return (
                <tr
                  key={status.id}
                  className="border-b border-border hover:bg-secondary/30 transition-colors last:border-b-0"
                >
                  <td className="p-4">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingName}
                        onChange={(event) => setEditingName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") handleSaveEdit(status);
                          if (event.key === "Escape") setEditingId(null);
                        }}
                        placeholder="Nome do estado"
                        className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        style={{
                          borderRadius: "var(--radius-button)",
                          fontSize: "var(--text-base)",
                        }}
                        autoFocus
                      />
                    ) : (
                      <span
                        className="text-foreground"
                        style={{ fontSize: "var(--text-base)" }}
                      >
                        {status.name}
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => handleSaveEdit(status)}
                            className="p-2 hover:bg-success/10 rounded transition-colors"
                            style={{ borderRadius: "var(--radius-button)" }}
                            title="Guardar"
                          >
                            <Checkmark20Regular className="w-5 h-5 text-success" />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-2 hover:bg-destructive/10 rounded transition-colors"
                            style={{ borderRadius: "var(--radius-button)" }}
                            title="Cancelar"
                          >
                            <Dismiss20Regular className="w-5 h-5 text-destructive" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleStartEdit(status)}
                            className="p-2 hover:bg-secondary rounded transition-colors"
                            style={{ borderRadius: "var(--radius-button)" }}
                            title="Editar"
                          >
                            <Edit20Regular className="w-5 h-5 text-foreground" />
                          </button>
                          <button
                            onClick={() => handleDelete(status)}
                            className="p-2 hover:bg-destructive/10 rounded transition-colors"
                            style={{ borderRadius: "var(--radius-button)" }}
                            title="Eliminar"
                          >
                            <Delete20Regular className="w-5 h-5 text-destructive" />
                          </button>
                          <button
                            onClick={() => handleMove(index, -1)}
                            disabled={index === 0}
                            className={`p-2 rounded transition-colors ${
                              index === 0
                                ? "opacity-30 cursor-not-allowed"
                                : "hover:bg-secondary"
                            }`}
                            style={{ borderRadius: "var(--radius-button)" }}
                            title="Mover para cima"
                          >
                            <ArrowUp20Regular className="w-5 h-5 text-foreground" />
                          </button>
                          <button
                            onClick={() => handleMove(index, 1)}
                            disabled={index === statuses.length - 1}
                            className={`p-2 rounded transition-colors ${
                              index === statuses.length - 1
                                ? "opacity-30 cursor-not-allowed"
                                : "hover:bg-secondary"
                            }`}
                            style={{ borderRadius: "var(--radius-button)" }}
                            title="Mover para baixo"
                          >
                            <ArrowDown20Regular className="w-5 h-5 text-foreground" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {statuses.length === 0 && (
          <div className="p-12 text-center">
            <p
              className="text-secondary-text"
              style={{ fontSize: "var(--text-base)" }}
            >
              Nenhum estado registado. Clique em "Adicionar" para criar o
              primeiro.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
